// StaleBanner — shown when workingForecastStale is true.
// Offers a single "Refresh Forecast" action that calls the refresh mutation
// and propagates the updated outlook back to the caller.

import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  useRefreshForecastInputs,
  getGetForecastOutlookQueryKey,
} from "@workspace/api-client-react";
import type { ForecastInputList, ForecastOutlook } from "@workspace/api-client-react";

interface StaleBannerProps {
  inputs: ForecastInputList;
  runId: string;
  onRefreshed: (outlook: ForecastOutlook) => void;
  isAuthoritative: boolean;
}

export function StaleBanner({ inputs, runId, onRefreshed, isAuthoritative }: StaleBannerProps) {
  const refreshMutation = useRefreshForecastInputs();
  const queryClient = useQueryClient();

  if (!inputs.workingForecastStale || isAuthoritative) return null;

  const handleRefresh = () => {
    refreshMutation.mutate({ runId, data: { actor: "ER" } }, {
      onSuccess: (updated) => {
        queryClient.setQueryData(getGetForecastOutlookQueryKey(runId), updated);
        onRefreshed(updated);
      },
    });
  };

  return (
    <div className="fi-stale-banner" role="status" aria-live="polite" data-testid="fi-stale-banner">
      <AlertTriangle size={14} aria-hidden="true" />
      <div>
        <b>Forecast Inputs Changed</b>
        <span>Source inputs have changed since the current Forecast was calculated.</span>
      </div>
      <div className="fi-stale-actions">
        <button
          className="tl-btn primary"
          onClick={handleRefresh}
          disabled={refreshMutation.isPending}
          aria-label="Refresh the forecast with latest inputs"
          data-testid="fi-refresh-forecast"
        >
          {refreshMutation.isPending
            ? <><Loader2 size={12} className="animate-spin" aria-hidden="true" /> Refreshing…</>
            : "Refresh Forecast"}
        </button>
      </div>
    </div>
  );
}
